'use strict';

/**
 * Pict-Provider-DocLakeAPI
 * ------------------------
 * Browser-side client for the Docs Lake (the one content system). Reads and
 * writes documents by PATH against /1.0/docs/* instead of ContentDocument rows.
 * Entity-to-doc links live in DocEntityLink (a plain table reached through
 * LinkAPI); this provider handles the doc bodies, the tree, and titles.
 *
 *   tree()              -> [DocNode...] for the current tenant (caches titles)
 *   titleFor(path)      -> the cached H1 title (or the file name)
 *   readDoc(path)       -> markdown/text body
 *   saveDoc(path, body) -> commit a platform edit, returns { Commit, Changed }
 *   deleteDoc(path)     -> remove a path (a commit)
 */

const libPictProvider = require('pict').ServiceProviderBase;
const libFrameworkClient = require('./FrameworkClient.js');

const _DefaultProviderConfiguration =
{
	ProviderIdentifier: 'DocLakeAPI',
	Dependencies: [],
	RoutePrefix: '/1.0/docs/'
};

class PictProviderDocLakeAPI extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		let tmpOptions = Object.assign({}, _DefaultProviderConfiguration, pOptions || {});
		super(pFable, tmpOptions, pServiceHash);
		this.base = this.options.RoutePrefix;
		this._titles = {};
		this._nodes = [];
	}

	tree()
	{
		return libFrameworkClient.getJSON(this.fable, this.base + 'tree')
			.then((pBody) =>
			{
				this._nodes = (pBody && pBody.Nodes) || [];
				this._titles = {};
				this._nodes.forEach((pN) => { this._titles[pN.Path] = pN.Title || pN.Name; });
				return this._nodes;
			})
			.catch(() => []);
	}

	nodes() { return this._nodes; }
	titleFor(pPath) { return this._titles[pPath] || String(pPath).slice(String(pPath).lastIndexOf('/') + 1); }

	readDoc(pPath)
	{
		return libFrameworkClient.getText(this.fable, this.base + String(pPath).replace(/^\//, '')).catch(() => '');
	}

	saveDoc(pPath, pContent)
	{
		return libFrameworkClient.putJSON(this.fable, this.base + 'save', { Path: pPath, Content: pContent });
	}

	deleteDoc(pPath)
	{
		return libFrameworkClient.delJSON(this.fable, this.base + String(pPath).replace(/^\//, ''));
	}

	// ─── structure operations (folders, move, reorder) ───────────────────
	// Create an empty folder (writes its _folder.json marker) -> { Path, Commit, Changed }.
	createFolder(pPath, pLabel)
	{
		return libFrameworkClient.postJSON(this.fable, this.base + 'folder', { Path: pPath, Label: pLabel || '' });
	}

	// Move or rename a document or folder. Pass either an explicit To path or a
	// ToFolder to drop the source into -> { From, To, Commit, Changed, LinksAffected }.
	moveNode(pFrom, pTo, pToFolder)
	{
		let tmpBody = { From: pFrom };
		if (pTo) { tmpBody.To = pTo; }
		if (pToFolder !== undefined && pToFolder !== null) { tmpBody.ToFolder = pToFolder; }
		return libFrameworkClient.postJSON(this.fable, this.base + 'move', tmpBody);
	}

	// Persist a folder's sibling order (drag-to-reorder). pParentPath '' is the root.
	reorder(pParentPath, pOrder)
	{
		return libFrameworkClient.postJSON(this.fable, this.base + 'reorder', { ParentPath: pParentPath || '', Order: pOrder || [] });
	}

	// Delete a document or (recursively) a folder -> { Path, Changed, LinksAffected }.
	deleteNode(pPath)
	{
		return libFrameworkClient.delJSON(this.fable, this.base + String(pPath).replace(/^\//, ''));
	}
}

module.exports = PictProviderDocLakeAPI;
module.exports.default_configuration = _DefaultProviderConfiguration;
